import React, { useEffect, useState } from 'react';
import { supervisedStudentApi } from '@/features/workspace/api';
import type { DepartmentDuty, MeetingConsultation } from '../types';
import '../u129-meetings.css';

interface MeetingsAndDutiesOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  STUDENT: 'Обучающийся под руководством',
  SUPERVISOR: 'Научный руководитель',
};

const AWAITING = new Set(['AWAITING_RESPONSE', 'PLANNED', 'scheduled']);

function roleLabel(code?: string | null): string {
  if (!code) return 'Научный руководитель';
  return ROLE_LABELS[code] || code;
}

function dutyParts(duty: DepartmentDuty): { date: string; time: string; title: string; instruction: string; code: string } {
  const raw = duty.date_time || [duty.date, duty.shift].filter(Boolean).join(' · ');
  const [date, time] = raw.split('·').map((part) => part.trim());
  return {
    date: date || raw,
    time: time || '',
    title: duty.title || duty.description || 'Дежурство',
    instruction: duty.instruction || duty.description || '',
    code: duty.duty_code || duty.id,
  };
}

export const MeetingsAndDutiesOverlay: React.FC<MeetingsAndDutiesOverlayProps> = ({
  isOpen,
  onClose,
}) => {
  const [meetings, setMeetings] = useState<MeetingConsultation[]>([]);
  const [duties, setDuties] = useState<DepartmentDuty[]>([]);
  const [notice, setNotice] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState({
    to_role: 'SUPERVISOR',
    date: '2026-08-18',
    time: '10:00',
    title: 'Академическая консультация',
    agenda: '',
  });

  useEffect(() => {
    if (!isOpen) return;
    Promise.all([
      supervisedStudentApi.meetings(),
      supervisedStudentApi.duties(),
    ])
      .then(([meetingRows, dutyRows]) => {
        setMeetings(meetingRows || []);
        setDuties(dutyRows || []);
      })
      .catch(() => setNotice('Не удалось открыть встречи и дежурства.'));
  }, [isOpen]);

  if (!isOpen) return null;

  const respondMeeting = async (meeting: MeetingConsultation, status: 'ACCEPTED' | 'DECLINED') => {
    setMeetings((rows) => rows.map((row) => (row.id === meeting.id ? { ...row, status } : row)));
    setNotice(status === 'ACCEPTED' ? 'Приглашение принято' : 'Приглашение отклонено');
    try {
      const saved = await supervisedStudentApi.respondMeeting(meeting.id, status);
      setMeetings((rows) => rows.map((row) => (row.id === meeting.id ? { ...row, ...saved } : row)));
    } catch (error) {
      setMeetings((rows) => rows.map((row) => (row.id === meeting.id ? meeting : row)));
      setNotice(error instanceof Error ? error.message : 'Не удалось сохранить ответ.');
    }
  };

  const respondDuty = async (duty: DepartmentDuty, status: string) => {
    setDuties((rows) => rows.map((row) => (row.id === duty.id ? { ...row, status } : row)));
    setNotice(
      status === 'COMPLETED'
        ? 'Дежурство отмечено выполненным'
        : status === 'CONFIRMED'
          ? 'Дежурство подтверждено'
          : 'Отказ сохранён',
    );
    try {
      const saved = await supervisedStudentApi.respondDuty(duty.id, status);
      setDuties((rows) => rows.map((row) => (row.id === duty.id ? { ...row, ...saved } : row)));
    } catch (error) {
      setDuties((rows) => rows.map((row) => (row.id === duty.id ? duty : row)));
      setNotice(error instanceof Error ? error.message : 'Не удалось сохранить дежурство.');
    }
  };

  const createMeeting = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const created = await supervisedStudentApi.createMeeting(draft);
      setMeetings((rows) => [created, ...rows]);
      setComposerOpen(false);
      setNotice('Приглашение отправлено');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Не удалось создать встречу.');
    }
  };

  return (
    <div className="u129-root" role="dialog" aria-modal="true">
      <header className="u129-top">
        <button type="button" onClick={onClose}>← Назад</button>
        <div>
          <h1>Встречи и дежурства</h1>
          <p>STUDENT · приглашения, подтверждение и дежурства</p>
        </div>
        <button type="button" onClick={onClose}>Закрыть ×</button>
      </header>
      <main className="u129-content">
        {notice ? <div className="u129-note u129-toast">{notice}</div> : null}
        <section className="u129-hero">
          <div>
            <h3>Встречи и дежурства</h3>
            <p>В одной карточке собраны приглашения научного руководителя, подтверждение участия, назначенные дежурства, инструкции и история.</p>
          </div>
          <div className="u129-toolbar">
            <button type="button" className="u129-btn gold" onClick={() => setComposerOpen(true)}>+ Создать встречу</button>
          </div>
        </section>

        <section className="u129-panel">
          <header>
            <h3>Встречи и дежурства</h3>
            <span className="u129-chip">{meetings.length}</span>
          </header>
          <div className="u129-body">
            {meetings.length === 0 ? <div className="u129-note">Встреч пока нет.</div> : meetings.map((meeting) => {
              const incoming = (meeting.to_role || 'STUDENT') === 'STUDENT';
              const waiting = AWAITING.has(meeting.status);
              return (
                <div className="u129-row" key={meeting.id}>
                  <div>
                    <strong>{meeting.title || meeting.topic}</strong>
                    <small>{meeting.meeting_code || meeting.id} · {meeting.meeting_date || meeting.scheduled_at} · {meeting.meeting_time || ''}</small>
                  </div>
                  <div>
                    <strong>{roleLabel(meeting.from_role)} → {roleLabel(meeting.to_role)}</strong>
                    <small>{meeting.agenda || meeting.notes || ''}</small>
                  </div>
                  <div>
                    <span className="u129-chip">{meeting.status}</span>
                    {incoming && waiting ? (
                      <>
                        {' '}
                        <button type="button" className="u129-btn mint" onClick={() => void respondMeeting(meeting, 'ACCEPTED')}>Принять</button>
                        {' '}
                        <button type="button" className="u129-btn red" onClick={() => void respondMeeting(meeting, 'DECLINED')}>Отклонить</button>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="u129-panel">
          <header>
            <div>
              <h3>Назначенные дежурства</h3>
              <p>Пример рабочего дежурства с подтверждением и фиксацией результата.</p>
            </div>
            <span className="u129-chip">{duties.length}</span>
          </header>
          <div className="u129-body">
            {duties.map((duty) => {
              const parts = dutyParts(duty);
              return (
                <div className="u129-row" key={duty.id}>
                  <div>
                    <strong>{parts.title}</strong>
                    <small>{parts.code} · {parts.date}{parts.time ? ` · ${parts.time}` : ''} · {duty.location}</small>
                  </div>
                  <div>
                    <strong>Инструкция</strong>
                    <small>{parts.instruction}</small>
                  </div>
                  <div>
                    <span className="u129-chip">{duty.status}</span>
                  </div>
                </div>
              );
            })}
            <div className="u129-toolbar">
              {duties[0] ? (
                <>
                  <button type="button" className="u129-btn mint" onClick={() => void respondDuty(duties[0], 'CONFIRMED')}>Подтвердить дежурство</button>
                  <button type="button" className="u129-btn red" onClick={() => void respondDuty(duties[0], 'DECLINED')}>Не могу участвовать</button>
                  <button type="button" onClick={() => void respondDuty(duties[0], 'COMPLETED')} className="u129-btn">Отметить выполненным</button>
                </>
              ) : null}
            </div>
          </div>
        </section>
      </main>

      {composerOpen ? (
        <>
          <div className="u129-backdrop" onClick={() => setComposerOpen(false)} />
          <section className="u129-modal" role="dialog" aria-label="Создать встречу">
            <header>
              <h3>Создать встречу</h3>
              <button type="button" className="u129-btn" onClick={() => setComposerOpen(false)}>×</button>
            </header>
            <form className="u129-body" onSubmit={(event) => void createMeeting(event)}>
              <div className="u129-grid two">
                <label className="u129-field">
                  <span>Получатель</span>
                  <select className="u129-select" value={draft.to_role} onChange={(event) => setDraft({ ...draft, to_role: event.target.value })}>
                    <option value="SUPERVISOR">Научный руководитель</option>
                  </select>
                </label>
                <label className="u129-field">
                  <span>Дата</span>
                  <input className="u129-input" type="date" required value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.target.value })} />
                </label>
                <label className="u129-field">
                  <span>Время</span>
                  <input className="u129-input" type="time" required value={draft.time} onChange={(event) => setDraft({ ...draft, time: event.target.value })} />
                </label>
                <label className="u129-field">
                  <span>Название</span>
                  <input className="u129-input" required value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
                </label>
              </div>
              <label className="u129-field">
                <span>Agenda</span>
                <textarea className="u129-textarea" value={draft.agenda} onChange={(event) => setDraft({ ...draft, agenda: event.target.value })} />
              </label>
              <button className="u129-btn gold" type="submit">Отправить приглашение</button>
            </form>
          </section>
        </>
      ) : null}
    </div>
  );
};
